import { useMemo, useState } from 'react'
import moment from 'moment'
import { useMediaQuery, useTheme } from '@paintscout/ui'
import { getPainterAccountAccess } from '@paintscout/util/users'
import { getWeekDays, getWeekStart } from './PainterScheduleView/dateUtils'
import type { PainterScheduleViewProps, TabValue } from './PainterScheduleView/types'
import { usePainterMenuContext } from '../PainterSchedulePage/PainterMenuContext'

export function usePainterScheduleView(props: PainterScheduleViewProps) {
  const { events = [] } = props
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'))

  const today = useMemo(() => moment(), [])
  const [weekOffset, setWeekOffset] = useState(0)
  const [selectedDay, setSelectedDay] = useState<moment.Moment>(today.clone())
  const [expandedJobId, setExpandedJobId] = useState<string | null | false>(null)
  const [activeTabs, setActiveTabs] = useState<Record<string, TabValue>>({})
  const [expandedCrewNotes, setExpandedCrewNotes] = useState<Set<string>>(new Set())
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set())
  const { menuOpen, setMenuOpen, notificationsOpen, setNotificationsOpen } = usePainterMenuContext()

  const weekStart = useMemo(() => getWeekStart(today, weekOffset), [today, weekOffset])
  const weekDays = useMemo(() => getWeekDays(weekStart), [weekStart])
  const painterAccess = useMemo(() => getPainterAccountAccess(props.permissions), [props.permissions])

  const showContactInfo = props.showContactInfo ?? (props.permissions ? painterAccess.canViewClientInfo : true)
  const showTasksNotesFiles =
    props.showTasksNotesFiles ?? (props.permissions ? painterAccess.canViewTasksNotesFiles : true)
  const canAddAdditionalWork =
    props.canAddAdditionalWork ?? (props.permissions ? painterAccess.canAddAdditionalWork : true)
  const showPrices = props.showPrices ?? (props.permissions ? painterAccess.canViewPrices : true)
  const showPayments = props.showPayments ?? (props.permissions ? painterAccess.canManagePayments : false)

  const daysWithEvents = useMemo(() => {
    const result = new Set<string>()
    for (const event of events) {
      const start = moment(event.startDate).startOf('day')
      const end = moment(event.endDate).startOf('day')
      for (const day of weekDays) {
        if (day.isBetween(start, end, 'day', '[]')) {
          result.add(day.format('YYYY-MM-DD'))
        }
      }
    }
    return result
  }, [events, weekDays])

  function shiftWeek(delta: number) {
    const nextOffset = weekOffset + delta
    if (nextOffset < -1 || nextOffset > 1) return
    setWeekOffset(nextOffset)
    setSelectedDay((day) => day.clone().add(delta * 7, 'days'))
  }

  function toggleSet(setter: React.Dispatch<React.SetStateAction<Set<string>>>, id: string) {
    setter((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const callbacks = {
    onDirections: props.onDirections,
    onCall: props.onCall,
    onMessage: props.onMessage,
    onAddAdditionalWork: props.onAddAdditionalWork,
    onAddTask: props.onAddTask,
    onAddNote: props.onAddNote,
    onAddFile: props.onAddFile,
    onAddPayment: props.onAddPayment,
    onOpenPayment: props.onOpenPayment,
    onAcceptSign: props.onAcceptSign,
    onEdit: props.onEditAdditionalWork,
    onDelete: props.onDeleteAdditionalWork,
    onViewMedia: props.onViewMedia
  }

  return {
    isDesktop,
    today,
    weekStart,
    weekOffset,
    weekDays,
    daysWithEvents,
    selectedDay,
    setSelectedDay,
    expandedJobId,
    setExpandedJobId,
    activeTabs,
    setActiveTabs,
    expandedCrewNotes,
    setExpandedCrewNotes,
    expandedProducts,
    setExpandedProducts,
    menuOpen,
    setMenuOpen,
    notificationsOpen,
    setNotificationsOpen,
    showContactInfo,
    showTasksNotesFiles,
    canAddAdditionalWork,
    showPrices,
    showPayments,
    shiftWeek,
    toggleSet,
    callbacks
  }
}
